import { createFileRoute } from "@tanstack/react-router";
import { formatISO } from "date-fns/formatISO";
import { isValid } from "date-fns/isValid";
import { parseISO } from "date-fns/parseISO";
import { ChartFilters } from "@/components/chart-filters";
import { DownloadsChart } from "@/components/charts/downloads";
import { SectionCards } from "@/components/section-cards";
import { SiteHeader } from "@/components/site-header";
import {
	buildQuickFiltersData,
	QUICK_FILTERS,
	useChartFilters,
	type QuickFilter,
} from "@/hooks/use-chart-filters";

type StatisticsSearch = {
	q?: QuickFilter;
	from?: string;
	to?: string;
};

export const Route = createFileRoute("/_app/")({
	component: Page,
	validateSearch: (search: Record<string, unknown>): StatisticsSearch => {
		const q = typeof search.q === "string" && QUICK_FILTERS.includes(search.q) ? search.q : "7d";
		const quickFilters = buildQuickFiltersData();
		const quickFilter = !["all", "custom"].includes(q)
			? quickFilters.find((f) => f.key === q)
			: undefined;

		return {
			q,
			from:
				typeof search.from === "string" && isValid(parseISO(search.from))
					? search.from
					: quickFilter?.value?.from
						? formatISO(quickFilter.value.from, { representation: "date" })
						: undefined,
			to:
				typeof search.to === "string" && isValid(parseISO(search.to))
					? search.to
					: quickFilter?.value?.to
						? formatISO(quickFilter.value.to, { representation: "date" })
						: undefined,
		};
	},
});

function Page() {
	const search = Route.useSearch();

	const chartFilters = useChartFilters({
		allStartDate: new Date("2025-01-01"),
		initialFilter: search.q,
		initialDate:
			search.from && search.to
				? { from: new Date(search.from), to: new Date(search.to) }
				: undefined,
	});

	return (
		<>
			<SiteHeader breadcrumbs={[{ key: "dashboard", label: "Dashboard" }]} />
			<div className="flex flex-1 flex-col">
				<div className="@container/main flex flex-1 flex-col gap-2">
					<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
						<SectionCards />
						<ChartFilters hook={chartFilters} className="px-4 lg:px-6" />
						<div className="px-4 lg:px-6">
							<DownloadsChart hook={chartFilters} />
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
